// SEO utility functions for creating friendly URLs

export const slugify = (str = '') =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Build SEO-friendly make URL: /toyota
 */
export const makePath = (make) =>
  `/${slugify(make?.name || make || '')}`;

/**
 * Build SEO-friendly model URL: /toyota/hilux
 */
export const modelPath = (make, model) =>
  `/${slugify(make?.name || make || '')}/${slugify(model?.name || model || '')}`;

/**
 * Convert a product name to a SEO-friendly URL slug
 * @param {string} name - Product name
 * @param {string} sku - Product SKU (fallback)
 * @returns {string} - SEO-friendly slug
 */
export const createProductSlug = (name, sku) => {
  if (!name && !sku) return 'product';
  const text = name || sku;
  return slugify(text).substring(0, 70).replace(/-+$/, '');
};

/**
 * Fallback identifier — returns null so createProductUrl gracefully falls back to /products.
 * The real HMAC token is always injected by the backend (product.uuid / product.slug_hash).
 * Never generate a guessable token on the client side.
 */
const generateProductHash = (_product) => null;

/**
 * Extract the primary (first) series code from a potentially multi-code series string.
 * e.g. 'AE112-AE111-KE125' -> 'ae112'
 */
const primarySeriesCode = (seriesName = '') => {
  if (!seriesName) return '';
  // Series codes are typically 2-8 char alphanumeric tokens separated by dashes
  const tokens = seriesName.split(/[-\s]+/);
  // Keep only the first token that looks like a chassis code (letters+digits, ≤8 chars)
  const first = tokens.find(t => /^[a-zA-Z0-9]{1,8}$/.test(t));
  return slugify(first || tokens[0] || '');
};

/**
 * Expand abbreviated glass type / position codes to full SEO-friendly words.
 * e.g. 'win' -> 'windscreen', 'dg' -> 'door-glass'
 */
const GLASS_TYPE_MAP = {
  win:  'windscreen',
  ws:   'windscreen',
  wsc:  'windscreen',
  wshld: 'windscreen',
  dg:   'door-glass',
  door: 'door-glass',
  rw:   'rear-window',
  rear: 'rear-window',
  qg:   'quarter-glass',
  qtr:  'quarter-glass',
  vent: 'vent-glass',
  vg:   'vent-glass',
  bg:   'back-glass',
  back: 'back-glass',
};

const expandGlassType = (raw = '') => {
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  return GLASS_TYPE_MAP[key] || slugify(raw);
};

/**
 * Build a compact keyword-rich URL slug from vehicle fitment data.
 * Make and model are NOT included — they live in the URL path (/:make/:model/).
 * Format: {primarySeries}-{startYear}-{endYear}-{bodyType}-{position}
 * e.g. rzn140-1997-2005-ute-front-windscreen
 */
const buildVehicleSlug = (product) => {
  const fitment   = (product.vehicleFitments || [])[0];
  const rawSeries = fitment?.series_name || product.series_name || '';
  const series    = primarySeriesCode(rawSeries);
  const bodyType  = slugify(fitment?.bodyType?.name  || product.bodyType?.name  || '');
  const rawPosition =
    fitment?.position?.name ||
    (typeof product.position === 'object' ? product.position?.name : product.position) ||
    '';
  const position  = expandGlassType(rawPosition);
  const startYear = fitment?.start_year || product.start_year || '';
  const endYear   = fitment?.end_year   || product.end_year   || '';

  const parts = [series, startYear, endYear, bodyType, position].filter(Boolean);
  if (parts.length > 0) {
    return parts.join('-').replace(/-+/g, '-').replace(/^-+|-+$/g, '').substring(0, 70);
  }
  // No fitment data — use product name or SKU as the slug
  return slugify(product.name || product.sku || 'auto-glass').substring(0, 70) || 'auto-glass';
};

/**
 * Derive the make and model slugs from a product object.
 */
export const productMakeModelSlugs = (product) => {
  const fitment = (product.vehicleFitments || [])[0];
  const make  = slugify(fitment?.make?.name  || product.make?.name  || 'vehicle');
  const model = slugify(fitment?.model?.name || product.model?.name || 'model');
  return { make, model };
};

/**
 * Create SEO-friendly product URL.
 * Desktop default: /:make/:model/{series}-{year}-{bodyType}-{position}-{xxxx-xxxx}
 *   e.g. /toyota/hilux/rzn140-1997-2005-ute-front-windscreen-0012-1289
 * Mobile (pass basePath='/mobile/products'): /mobile/products/{slug}-{xxxx-xxxx}
 */
export const createProductUrl = (product, options = {}) => {
  if (!product || !product.id) {
    return options.basePath || '/products';
  }

  const vehicleSlug = buildVehicleSlug(product);
  const identifier  = product.slug_hash || product.uuid || product.product_uuid || generateProductHash(product);

  // Mobile path: flat /mobile/products/slug-identifier
  if (options.basePath) {
    return `${options.basePath}/${vehicleSlug}-${identifier}`;
  }

  // Desktop: /:make/:model/slug-identifier
  const { make, model } = productMakeModelSlugs(product);
  return `/${make}/${model}/${vehicleSlug}-${identifier}`;
};

/**
 * Extract product identifier from SEO-friendly URL slug
 * Expects format: product-name-abc1-23de, numeric ID: 12345, or raw SKU: OZ_OZ082
 */
export const extractProductIdFromSlug = (slug) => {
  if (!slug) return null;
  
  // Handle numeric ID directly (for accessory links like /products/12345)
  if (/^\d+$/.test(slug)) {
    return slug;
  }
  
  // Extract the UUID-like identifier (last part after the last dash)
  const parts = slug.split('-');
  if (parts.length >= 2) {
    // The identifier should be the last two parts joined (abc1-23de)
    const lastTwoParts = parts.slice(-2).join('-');
    
    // Validate it looks like our UUID format (4 hex chars - 4 hex chars)
    if (/^[a-f0-9]{4}-[a-f0-9]{4}$/i.test(lastTwoParts)) {
      return lastTwoParts.toLowerCase();
    }
  }
  
  // Return raw slug as fallback for SKU lookup (backend supports SKU/NAGS search)
  return slug;
};

/**
 * Create breadcrumb-friendly product title
 * @param {object} product - Product object
 * @returns {string} - Formatted product title for breadcrumbs
 */
export const createProductBreadcrumbTitle = (product) => {
  if (!product) return 'Product';
  
  const parts = [];
  
  if (product.make?.name) parts.push(product.make.name);
  if (product.model?.name) parts.push(product.model.name);
  if (product.position?.name) parts.push(product.position.name);
  
  // Fall back to SKU, not raw product.name — that field can hold internal
  // fitment/installer notes rather than a clean customer-facing name.
  return parts.length > 0 ? parts.join(' ') : (product.sku || 'Product');
};

// ── YEAR RANGE EXPANSION ──────────────────────────────────────────────────
// Expands a start/end year range into individual years so Google can index
// (and match) searches for a specific model year, e.g. "RAV4 2024 windscreen".
// NOTE: this logic is duplicated in backend/seoPrerender.js since frontend and
// backend are separate packages — keep both implementations in sync.
const MAX_YEARS_IN_LIST = 30; // safety cap against bad data (e.g. start_year 1900)

/**
 * @param {number|string} startYear
 * @param {number|string|null} endYear - null/undefined means open-ended ("2022+"),
 *   resolved to the current year so it advances automatically each year.
 * @returns {{ years: number[], yearString: string, isCapped: boolean }}
 */
export const expandYearRange = (startYear, endYear) => {
  const start = parseInt(startYear, 10);
  if (!start || isNaN(start)) return { years: [], yearString: '', isCapped: false };

  const end = endYear ? parseInt(endYear, 10) : new Date().getFullYear();
  if (isNaN(end) || end < start) return { years: [start], yearString: String(start), isCapped: false };

  const span = end - start + 1;
  const isCapped = span > MAX_YEARS_IN_LIST;
  const cappedEnd = isCapped ? start + MAX_YEARS_IN_LIST - 1 : end;

  const years = [];
  for (let y = start; y <= cappedEnd; y++) years.push(y);

  return { years, yearString: years.join(' '), isCapped };
};

// Title-safe year text. Google truncates meta titles at ~55-60 visible chars, so
// years must sit as early in the title as possible — see createProductMetaTitle,
// which places this right after make/model instead of appending it at the end.
// Full list for <=6 years; longer ranges use first-3/last-2 + ellipsis. Keywords/
// description/JSON-LD always get the complete, untruncated year list regardless.
const buildTitleYearText = (startYear, endYear) => {
  const { years } = expandYearRange(startYear, endYear);
  if (years.length === 0) return '';
  if (years.length === 1) return String(years[0]);
  if (years.length <= 6) return years.join(' ');
  return `${years.slice(0, 3).join(' ')} ... ${years.slice(-2).join(' ')}`;
};

/**
 * Resolve the primary vehicle fitment data from a product object.
 * Handles both flat product fields and nested vehicleFitments array.
 */
const resolveFitment = (product) => {
  const fitment = (product.vehicleFitments || [])[0];
  return {
    make:      fitment?.make?.name      || product.make?.name      || '',
    model:     fitment?.model?.name     || product.model?.name     || '',
    series:    fitment?.series_name     || product.series_name     || '',
    bodyType:  fitment?.bodyType?.name  || product.bodyType?.name  || '',
    position:  fitment?.position?.name  || (typeof product.position === 'object' ? product.position?.name : product.position) || '',
    startYear: fitment?.start_year      || product.start_year      || '',
    endYear:   fitment?.end_year        || product.end_year        || '',
  };
};

/**
 * Generate alt text for product images: "Toyota RAV4 2022 2023 2024 2025 2026 Windscreen"
 */
export const createProductImageAlt = (product) => {
  if (!product) return 'Auto Glass Product';

  const { make, model, position, startYear, endYear } = resolveFitment(product);
  const { years } = startYear ? expandYearRange(startYear, endYear) : { years: [] };
  const yearPart = years.length > 1 ? years.join(' ') : (years[0] ? String(years[0]) : '');

  const alt = [make, model, yearPart, position].filter(Boolean).join(' ').trim();
  // Fall back to SKU, not raw product.name — that field can hold internal
  // fitment/installer notes rather than a clean customer-facing name.
  return alt || (product.sku || 'Auto Glass Product');
};

/**
 * Generate meta title for product page (SEO):
 * "TOYOTA RAV4 2022 2023 2024 2025 2026 SUV 5 DOOR Windscreen | Auto Glass Shop"
 * Years sit right after make/model (not appended at the end) so they land inside
 * Google's visible ~55-60 char truncation window regardless of which year in the
 * range someone searched for.
 */
export const createProductMetaTitle = (product) => {
  if (!product) return 'Auto Glass Product | Auto Glass Shop';

  const { make, model, bodyType, position, startYear, endYear } = resolveFitment(product);
  const yearText = startYear ? buildTitleYearText(startYear, endYear) : '';

  const descriptor = [make, model, yearText, bodyType, position]
    .filter(Boolean)
    .join(' ');

  // Fall back to SKU, not raw product.name — that field can hold internal
  // fitment/installer notes rather than a clean customer-facing name.
  if (!descriptor) return `${product.sku || 'Auto Glass Product'} | Auto Glass Shop`;

  return `${descriptor} | Auto Glass Shop`.replace(/  +/g, ' ');
};

/**
 * Generate meta description for product page (SEO)
 */
export const createProductMetaDescription = (product) => {
  if (!product) return 'Quality auto glass products for your vehicle at Auto Glass Shop.';

  const { make, model, bodyType, position, startYear, endYear } = resolveFitment(product);
  const yearRange = startYear
    ? (endYear && endYear !== startYear ? `${startYear} to ${endYear}` : endYear ? `${startYear}` : `from ${startYear}`)
    : '';

  const vehicleDesc = [make, model, bodyType, yearRange].filter(Boolean).join(' ');

  let description = vehicleDesc
    ? `OEM-quality replacement ${position || 'auto glass'} for ${vehicleDesc}. `
    : `OEM-quality ${position || 'auto glass'} replacement part. `;

  // Add explicit individual years when there's more than one — meta description has
  // more room than the title, so this is where the full-ish year list lives.
  if (startYear) {
    const { years } = expandYearRange(startYear, endYear);
    if (years.length > 1) {
      const yearsForDesc = years.length <= 6 ? years : [...years.slice(0, 3), '\u2026', ...years.slice(-2)];
      description += `(${yearsForDesc.join(', ')} models) `;
    }
  }

  description += 'In stock. Fast dispatch Sydney & Melbourne. Trade pricing available.';
  const cleaned = description.replace(/  +/g, ' ').trim();
  if (cleaned.length <= 160) return cleaned;
  const truncated = cleaned.substring(0, 160);
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > 100 ? truncated.substring(0, lastSpace) : truncated;
};

/**
 * Generate meta keywords for product page (SEO)
 * Produces phrases like: "replacement windscreen toyota hilux,toyota hilux windscreen replacement,..."
 */
export const createProductKeywords = (product) => {
  if (!product) return '';

  const { make, model, bodyType, position, startYear, endYear } = resolveFitment(product);

  const makeLower     = (make    || '').toLowerCase();
  const modelLower    = (model   || '').toLowerCase();
  const posLower      = (position || 'auto glass').toLowerCase();
  const bodyLower     = (bodyType || '').toLowerCase();

  const glassSynonyms = ['auto glass', 'autoglass', 'automotive glass'];
  const actionSynonyms = ['replacement', 'repair', 'fitting'];

  const keywords = new Set();

  if (makeLower && modelLower) {
    keywords.add(`replacement ${posLower} ${makeLower} ${modelLower}`);
    keywords.add(`${makeLower} ${modelLower} ${posLower} replacement`);
    keywords.add(`${makeLower} ${modelLower} ${posLower}`);
    keywords.add(`buy ${posLower} ${makeLower} ${modelLower}`);

    glassSynonyms.forEach(syn => {
      keywords.add(`${makeLower} ${modelLower} ${syn}`);
      keywords.add(`${syn} ${makeLower} ${modelLower}`);
      keywords.add(`${makeLower} ${modelLower} ${syn} replacement`);
      keywords.add(`${syn} replacement ${makeLower} ${modelLower}`);
    });

    actionSynonyms.forEach(act => {
      keywords.add(`${posLower} ${act} ${makeLower} ${modelLower}`);
      keywords.add(`${makeLower} ${modelLower} ${posLower} ${act}`);
    });

    if (bodyLower) {
      keywords.add(`${makeLower} ${modelLower} ${bodyLower} ${posLower}`);
    }

    if (startYear) {
      const { years } = expandYearRange(startYear, endYear);
      years.forEach(y => {
        keywords.add(`${makeLower} ${modelLower} ${y} ${posLower}`);
        keywords.add(`${makeLower} ${modelLower} ${posLower} ${y}`);
        keywords.add(`${makeLower} ${modelLower} ${y} ${posLower} replacement`);
        keywords.add(`${makeLower} ${modelLower} ${posLower} replacement ${y}`);
        keywords.add(`${posLower} replacement ${makeLower} ${modelLower} ${y}`);
        keywords.add(`${makeLower} ${modelLower} ${y} auto glass`);
        keywords.add(`${makeLower} ${modelLower} auto glass ${y}`);
        keywords.add(`${makeLower} ${modelLower} ${y} ${posLower} repair`);
        keywords.add(`${makeLower} ${modelLower} ${posLower} repair ${y}`);
        keywords.add(`${makeLower} ${modelLower} ${y} ${posLower} fitting`);
        keywords.add(`${makeLower} ${modelLower} ${posLower} fitting ${y}`);
      });
    }
  } else if (makeLower) {
    keywords.add(`${makeLower} ${posLower} replacement`);
    keywords.add(`replacement ${posLower} ${makeLower}`);
    keywords.add(`${makeLower} auto glass`);
    glassSynonyms.forEach(syn => keywords.add(`${makeLower} ${syn}`));
  }

  keywords.add(`${posLower} replacement`);
  keywords.add(`auto glass replacement`);
  keywords.add(`autoglass replacement`);
  keywords.add(`windscreen replacement australia`);
  keywords.add(`windscreen repair`);
  keywords.add(`windscreen fitting`);
  keywords.add(`auto glass repair`);

  return [...keywords].join(',');
};

/**
 * Validate product identifier format
 */
export const isValidProductIdentifier = (identifier) => {
  if (!identifier || typeof identifier !== 'string') return false;
  
  // Check for UUID-like format: abc1-23de
  if (/^[a-f0-9]{4}-[a-f0-9]{4}$/i.test(identifier)) return true;
  
  // Accept numeric IDs (for accessory product links)
  if (/^\d+$/.test(identifier)) return true;
  
  // Accept any non-empty string as valid identifier (backend supports SKU/NAGS lookup)
  return identifier.trim().length > 0;
};
